import {
  BadgePercent,
  Bell,
  Bike,
  DollarSign,
  FileText,
  MapPin,
  MessageSquare,
  PlugZap,
  Plug,
  Settings,
  Utensils,
  Workflow,
} from 'lucide-react';
import { EXTERNAL_AGENT_CHANNEL_AVAILABLE } from '@/types/sell-channel';
import type { MasterClientChannels } from '@/types/auth';
import type { SettingsSessionMode, TabAccess } from './settingsAccess';
import { getSectionAccess } from './settingsAccess';

/**
 * The settings sections, in nav order. Each one is a route under `/settings/` (spec §6.2) —
 * there is no tab state any more, so a deep link, a browser back and the dashboard's
 * `returnTo` (K8) all land on the same place.
 *
 * `tag` says which sell channel the section belongs to: a section whose channel is off does not
 * exist for that tenant. `shared` sections apply to every commerce tenant.
 */
export type SettingsSectionId =
  | 'general'
  | 'catalog'
  | 'discounts'
  | 'locations'
  | 'payments'
  | 'pos'
  | 'delivery'
  | 'policies'
  | 'notifications'
  | 'whatsapp'
  | 'whatsapp/flows'
  | 'external';

export type SettingsSectionTag = 'shared' | 'whatsapp' | 'web' | 'external';

/** Nav grouping only — which heading a section sits under in the settings sidebar. */
export type SettingsSectionGroup = 'business' | 'selling' | 'channels' | 'integrations';

/** Group order in the nav. A group whose sections are all hidden is not rendered. */
export const SETTINGS_SECTION_GROUPS: SettingsSectionGroup[] = ['business', 'selling', 'channels', 'integrations'];

export interface SettingsSection {
  id: SettingsSectionId;
  /** Path segment under `/settings/` — `whatsapp/flows` is two segments on purpose. */
  path: string;
  tag: SettingsSectionTag;
  group: SettingsSectionGroup;
  /** i18n key in the `brand` namespace. */
  labelKey: string;
  /** i18n key in the `brand` namespace: one plain sentence on what the section is for. */
  descriptionKey: string;
  icon: typeof Settings;
}

/**
 * Nav order: grouped by what a merchant is trying to do rather than by channel. `general` stays
 * first — it is `defaultSettingsSection`'s fallback for a tenant without WhatsApp.
 */
export const SETTINGS_SECTIONS: SettingsSection[] = [
  section('general', 'general', 'shared', 'business', Settings),
  section('locations', 'locations', 'shared', 'business', MapPin),
  section('policies', 'policies', 'shared', 'business', FileText),
  section('catalog', 'catalog', 'shared', 'selling', Utensils),
  section('discounts', 'discounts', 'shared', 'selling', BadgePercent),
  section('payments', 'payments', 'shared', 'selling', DollarSign),
  section('whatsapp', 'whatsapp', 'whatsapp', 'channels', MessageSquare),
  {
    ...section('whatsapp/flows', 'whatsapp/flows', 'whatsapp', 'channels', Workflow),
    labelKey: 'sections.flows',
    descriptionKey: 'sectionDescriptions.flows',
  },
  section('external', 'external', 'external', 'channels', Plug),
  section('pos', 'pos', 'shared', 'integrations', PlugZap),
  section('delivery', 'delivery', 'shared', 'integrations', Bike),
  section('notifications', 'notifications', 'shared', 'integrations', Bell),
];

function section(
  id: SettingsSectionId,
  path: string,
  tag: SettingsSectionTag,
  group: SettingsSectionGroup,
  icon: typeof Settings,
): SettingsSection {
  return { id, path, tag, group, labelKey: `sections.${id}`, descriptionKey: `sectionDescriptions.${id}`, icon };
}

/** Whether the tenant has the channel a section belongs to. `shared` always applies. */
function channelEnabledForTag(tag: SettingsSectionTag, channels: MasterClientChannels | null | undefined): boolean {
  switch (tag) {
    case 'shared':
      return true;
    case 'whatsapp':
      return channels?.whatsapp.enabled ?? false;
    case 'web':
      return channels?.web.enabled ?? false;
    case 'external':
      // Gated on the BUILD as well as on the tenant: the channel is "coming soon"
      // (`@/types/sell-channel`), and a section that configures an allow-list for a connector no
      // host can reach is a nav entry that leads nowhere. munchies-api already reports the channel
      // off, so this is what makes the section's absence independent of the payload.
      return EXTERNAL_AGENT_CHANNEL_AVAILABLE && (channels?.externalAgent.enabled ?? false);
  }
}

export interface SectionVisibility extends SettingsSection {
  access: Exclude<TabAccess, 'hidden'>;
}

/**
 * Sections that also depend on the brand rather than only on the session: Discounts is an
 * order-taking concern, so a brand without ORDERS has nothing to discount. `undefined` means the
 * capability has not loaded yet — the section stays visible then, so a deep link into it holds
 * instead of being bounced to another section a tick before the answer arrives.
 */
function capabilityEnabledForSection(id: SettingsSectionId, hasOrdersCapability: boolean | undefined): boolean {
  if (id !== 'discounts') return true;
  return hasOrdersCapability !== false;
}

/**
 * The sections this session can see, with the access each one grants. Three independent gates:
 * the channel (a section for a channel that is off does not exist), the brand's capabilities, and
 * the session mode (`settingsAccess.ts`). An aggregate session hides every section, so an empty
 * result means "this session has no settings surface" and the route says so instead of rendering
 * a shell.
 */
export function visibleSettingsSections(
  mode: SettingsSessionMode,
  channels: MasterClientChannels | null | undefined,
  hasOrdersCapability?: boolean,
): SectionVisibility[] {
  const access = getSectionAccess(mode);
  return SETTINGS_SECTIONS.filter(
    (section) =>
      channelEnabledForTag(section.tag, channels) &&
      capabilityEnabledForSection(section.id, hasOrdersCapability) &&
      access[section.id] !== 'hidden',
  ).map((section) => ({ ...section, access: access[section.id] as Exclude<TabAccess, 'hidden'> }));
}

/**
 * Where `/settings` lands. The WhatsApp section is the day-to-day surface (the simplified agent
 * view: messages, rules, behaviour), so a tenant that has it opens there; everyone else opens the
 * first section in nav order. Also the fallback for an unknown or currently-hidden section path.
 */
export function defaultSettingsSection(sections: SectionVisibility[]): SectionVisibility | undefined {
  return sections.find((section) => section.id === 'whatsapp') ?? sections[0];
}
